import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { UtilityService } from '@eforall/common';
import { GridContentWidget, PageTabsMainTabGridSize } from '@eforall/dashboard';
import { Event } from '@interfaces';
import { DomainDataService } from '../../../../../../services';
import { AcceleratorAreaService } from '../../../accelerator-area.service';
import { AccDeliveryService } from '../../acc-delivery.service';
import { getSetup } from './grid/get-setup';

export interface SurveysBySpecialistRow {
	accSessionSpecialistSurveyResponseId: number,
	accSessionAttendeeId: number,
	specialistPersonId: number,
	topicId: number,
	eventId: number,
	event: Event,
	sessionStartUTC: number,
	accVentureId: number,
	ventureName: string,
	entrepreneurPersonId: number,
	entrepreneurName: string,
	topicNames: string,
	topic: string,
	specialistName: string,
	professionalismBucket: string,
	knowledgeBucket: string,
	effectivenessBucket: string,
	other: string | undefined,
	specialistSurveyProfessionalismScore: number,
	specialistSurveyKnowledgeScore: number,
	specialistSurveyEffectivenessScore: number,
	createdUTC: number,
	updatedUTC: number,
}

@Component({
	selector: 'acc-surveys-by-specialist-view-part',
	standalone: true,
	imports: [GridContentWidget],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './surveys-by-specialist-view.part.html',
})
export class AccSurveysBySpecialistViewPart {

	private readonly util = inject(UtilityService);
	private readonly pageService = inject(AccDeliveryService);
	private readonly areaService = inject(AcceleratorAreaService);
	private readonly domain = inject(DomainDataService);

	public setup = getSetup(this.util);

	public pageTabsGridSize = computed<PageTabsMainTabGridSize | undefined>(() => {
		if (this.pageService.layout()) return { layout: this.pageService.layout()!, viewSelector: true };
		return undefined;
	});

	public surveysBySpecialist = computed<SurveysBySpecialistRow[]>(() => {
		const data = this.areaService.data();
		if (!data) return [];


		const events = this.areaService.data()?.events ?? [];

		const ventures = data.ventures;
		const sessions = data.sessions;
		const sessionTopics = data.sessionTopics;
		const accTopics = data.accTopics;
		const sessionAttendees = data.sessionAttendees;
		const sessionSurveyResults = data.sessionSurveyResponses;
		const sessionTopicSurveyResults = data.topicSurveyResponses;
		const sessionSpecialistSurveyResults = data.specialistSurveyResponses;

		const ddTopics = this.domain.data().topics;

		return sessionSpecialistSurveyResults.map(survey => {

			const sessionTopicSurveyResponse = sessionTopicSurveyResults.find(s => s.accSessionTopicSurveyResponseId == survey.accSessionTopicSurveyResponseId)!;
			const sessionSurveyResponse = sessionSurveyResults.find(s => s.accSessionSurveyResponseId == sessionTopicSurveyResponse.accSessionSurveyResponseId)!;

			const event = events.find(e => e.eventId == sessionSurveyResponse.eventId)!;
			const accSession = sessions.find(e => e.eventId == event.eventId)!;

			//
			// Get the attendee records for this session
			//
			const attendee = sessionAttendees.find(a => a.accSessionAttendeeId == sessionSurveyResponse.accSessionAttendeeId && !!a.accVentureId)!;

			const accVenture = ventures.find(venture => venture.accVentureId == attendee.accVentureId)!;

			//
			// Topics
			//
			const accSessionTopics = sessionTopics.filter(sessionTopic => sessionTopic.accSessionId == accSession.accSessionId);
			const accTopicIds = accSessionTopics.map(topic => topic.accTopicId);
			const sessionTopicIds = accTopics.filter(topic => accTopicIds.includes(topic.accTopicId)).map(topic => topic.topicId);


			const row: SurveysBySpecialistRow = {
				eventId: sessionSurveyResponse.eventId,
				event,
				topicNames: sessionTopicIds.length ? ddTopics.filter(topic => sessionTopicIds.includes(topic.topicId)).map(topic => topic.name).sort((a, b) => a > b ? 1 : -1).join(', ') : '',
				accSessionAttendeeId: sessionSurveyResponse.accSessionAttendeeId,
				accSessionSpecialistSurveyResponseId: survey.accSessionSpecialistSurveyResponseId,
				topicId: sessionTopicSurveyResponse.topicId,
				topic: ddTopics.find(topic => topic.topicId == sessionTopicSurveyResponse.topicId)?.name || '',
				sessionStartUTC: event.startUTC,
				accVentureId: accVenture.accVentureId,
				ventureName: accVenture.ventureName,
				entrepreneurPersonId: attendee.personId,
				entrepreneurName: attendee.personName,
				effectivenessBucket: this.util.nps.getBucket(survey.effectiveness),
				knowledgeBucket: this.util.nps.getBucket(survey.knowledge),
				professionalismBucket: this.util.nps.getBucket(survey.professionalism),
				other: survey.other,
				createdUTC: sessionSurveyResponse.createdUTC,
				specialistSurveyEffectivenessScore: survey.effectiveness,
				specialistSurveyKnowledgeScore: survey.knowledge,
				specialistSurveyProfessionalismScore: survey.professionalism,
				updatedUTC: survey.updatedUTC,
				specialistName: survey.personName,
				specialistPersonId: survey.personId,
			};

			return row;
		});

	});
}